import { Link } from 'react-router-dom'
import { Anchor, Card, Text, Badge, Group, Stack } from '@mantine/core'
import type { RunCardView } from '../../hooks/useEtlActivity'
import { NOT_MEASURED, exactNumber } from '../../utils/format'

interface ModuleCardProps {
  /** One SERVED run with a live heartbeat, enriched with this session's hub counters. */
  run: RunCardView
}

/**
 * One live run. Its figures are the server's ROW counts — finished, running, stale, not started — and
 * deliberately not a percent: the run's recorded total is written once at creation, and a resume can
 * add rows of another job kind, so finished-over-total can pass 100%. Not started is withheld (a dash)
 * exactly when that has happened.
 *
 * It used to be built from OrchestratorStarted hub events, which the live ELT path never publishes,
 * so no card ever appeared; now every live run has one, from the page's first poll.
 */
export function ModuleCard({ run }: ModuleCardProps) {
  const displayName = run.orchestratorName
    .replace('EtlOrchestrator', '')
    .replace(/([A-Z])/g, ' $1')
    .trim() || run.orchestratorName

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      data-run={run.executionId}
      style={{
        background: 'var(--gradient-card)',
        border: '1px solid var(--color-accent-primary)',
        transition: 'border-color 0.3s ease',
      }}
    >
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fw={600} size="md" truncate>
              {displayName}
            </Text>
            {/* A null module is an entity job or the whole warehouse; the run records no more than that. */}
            <Text size="xs" c="dimmed" truncate>
              {run.module ? `${run.module} · ${run.runKey}` : run.runKey}
            </Text>
          </Stack>
          <Badge color="blue" variant="light" size="sm">
            Running
          </Badge>
        </Group>

        {/* Entity rows, as the server counts them */}
        <Group gap="md" data-counts="entity-rows">
          <Text size="xs" c="dimmed">
            <Text span fw={600} c="var(--color-text-primary)">{exactNumber(run.entitiesFinished)}</Text> finished
          </Text>
          <Text size="xs" c="dimmed">
            <Text span fw={600} c="var(--color-text-primary)">{exactNumber(run.entitiesRunning)}</Text> running
          </Text>
          {run.entitiesStale > 0 && (
            <Text size="xs" c="dimmed">
              <Text span fw={600} c="var(--status-warning-fg)">{exactNumber(run.entitiesStale)}</Text> stale
            </Text>
          )}
          <Text
            size="xs"
            c="dimmed"
            title={run.countsMixResumedJobs
              ? 'A resume added rows of another job kind, so the total recorded at creation no longer describes them.'
              : undefined}
          >
            <Text span fw={600} c="var(--color-text-primary)">
              {run.entitiesNotStarted == null ? NOT_MEASURED : exactNumber(run.entitiesNotStarted)}
            </Text> not started
          </Text>
        </Group>

        {/* What is in flight in this run */}
        {run.current.map((entity) => (
          <Group key={entity.entityRunId} gap="sm" align="center" wrap="nowrap">
            {/* Pulsing dot */}
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--color-accent-primary)',
                boxShadow: '0 0 6px var(--color-accent-primary)',
                animation: 'pulse 1.5s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text size="sm" fw={500} truncate>
                {entity.entityName}
              </Text>
              <Text size="xs" c="dimmed">
                {entity.phase ?? NOT_MEASURED}
                {entity.rowsThisAttempt != null && ` - ${exactNumber(entity.rowsThisAttempt)} rows so far this attempt`}
              </Text>
            </Stack>
          </Group>
        ))}

        <Anchor component={Link} to={`/execution-history/${encodeURIComponent(run.executionId)}`} size="xs">
          Open in Execution History
        </Anchor>
      </Stack>
    </Card>
  )
}
